'use client'

import React from 'react'
import Link from 'next/link'
import { SectionWrapper } from '@/components/SectionWrapper'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import { cn } from '@/lib/utils'
import ProductGallery from '@/components/ui/ProductGallery'
import LinkWithIcon from '@/components/ui/LinkWithIcon'
import RichText from '@/components/RichText'
import { Media } from '@/components/Media'
import { extractLinkData, getLinkHref } from '@/components/Link'
import type { ProductOverviewSectionBlock, Media as MediaType } from '@/payload-types'

export interface ProductOverviewSectionClientProps {
  id: string
  title: string
  subtitle?: string
  text?: ProductOverviewSectionBlock['text']
  products: Array<{
    id: string
    title: string
    shortDescription: string
    image: any
    href: string
    displayOrder: number
    slug?: string
  }>
  ctaLink?: ProductOverviewSectionBlock['ctaLink']
  theme: 'white' | 'light-gray' | 'dark'
  settings?: {
    background?: 'white' | 'light-gray'
    isDarkMode?: boolean
    padding?: 'md' | 'lg'
  }
}

export default function ProductOverviewSectionClient({
  id,
  title,
  subtitle,
  text,
  products,
  ctaLink,
  theme = 'white',
  settings,
}: ProductOverviewSectionClientProps) {
  // ProductOverviewSection settings already use 'background' design token, not 'backgroundShade'
  const _background = settings?.background || (theme === 'light-gray' ? 'light-gray' : 'white')
  const isDarkMode = settings?.isDarkMode || theme === 'dark'
  const _padding = settings?.padding || 'md'
  const scrollMarginClass = 'scroll-mt-24 md:scroll-mt-32'
  const isDark = theme === 'dark'

  const isSingleProduct = products.length === 1
  const singleProductImage =
    isSingleProduct && typeof products[0].image === 'object' && products[0].image !== null
      ? (products[0].image as MediaType)
      : null

  const Title = (
    <MotionFadeIn initialY={24} duration={0.8}>
      <div className={cn(isSingleProduct ? 'mb-4' : 'mb-8')}>
        <div className="flex flex-col gap-2">
          <h2 className={cn('heading-lg', isDark ? 'on-dark' : 'text-primary')}>{title}</h2>
          {subtitle && <p className="subtitle-lg text-brand">{subtitle}</p>}
        </div>
      </div>
    </MotionFadeIn>
  )

  const Body = text ? (
    <MotionFadeIn initialY={20} duration={0.8} delay={0.05}>
      <div className={cn('max-w-4xl', isSingleProduct ? 'mb-4' : 'mb-8')}>
        <RichText
          data={text}
          enableGutter={false}
          enableProse={true}
          className="text-base md:text-lg"
        />
      </div>
    </MotionFadeIn>
  ) : null

  const Products =
    products && products.length > 0 && !isSingleProduct ? (
      <MotionFadeIn initialY={20} duration={0.8} delay={0.1}>
        <ProductGallery products={products} className="mb-8" sourceBlock="productOverviewSection" />
      </MotionFadeIn>
    ) : null

  const CTALink = ctaLink ? (
    <MotionFadeIn initialY={20} duration={0.8} delay={isSingleProduct ? 0.1 : 0.15}>
      <div className={cn('flex justify-start', isSingleProduct ? 'mt-4' : 'mt-8')}>
        <LinkWithIcon link={ctaLink} variant="link" color={isDark ? 'white' : 'brand'} size="md" />
      </div>
    </MotionFadeIn>
  ) : null

  // Determine section background - use settings if provided, otherwise fall back to theme
  // ProductOverviewSection settings use 'background' design token directly
  let sectionBackground: 'white' | 'light-gray' | 'transparent' = 'white'
  if (settings?.background) {
    sectionBackground = settings.background as 'white' | 'light-gray'
  } else if (theme === 'light-gray') {
    sectionBackground = 'light-gray'
  } else if (theme === 'dark') {
    sectionBackground = 'transparent'
  }

  // Single product layout: image left, text right (image top on mobile).
  // Entire card is one link — title, image, and CTA all go to the same place.
  // Resolve outer href: prefer configured ctaLink, fall back to the product page.
  const resolvedCtaHref =
    ctaLink && ctaLink.enabled !== false ? getLinkHref(extractLinkData(ctaLink)) : null
  const singleProductHref = resolvedCtaHref || products[0]?.href || '#'
  const ctaEnabled = Boolean(ctaLink && ctaLink.enabled !== false)
  const ctaLabel = ctaLink?.label || 'Zur Produktseite'
  const ctaOpensNewTab = Boolean(ctaLink?.openInNewTab)

  const singleProductContent = isSingleProduct && singleProductImage ? (
    <Link
      href={singleProductHref}
      {...(ctaOpensNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="group grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-12 items-center no-underline"
      data-card-type="product"
      data-card-slug={products[0]?.slug}
      data-card-block="productOverviewSection"
    >
      {/* Product image */}
      <MotionFadeIn initialY={20} duration={0.8} className="md:col-span-2">
        <div className="relative aspect-[4/5] w-full overflow-hidden">
          <div className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-[1.04]">
            <Media
              resource={singleProductImage}
              fill
              imgClassName="object-cover"
              size="(max-width: 768px) 100vw, 40vw"
              alt={singleProductImage.alt || products[0].title || 'Produktbild'}
            />
          </div>
        </div>
      </MotionFadeIn>

      {/* Text content */}
      <div className="md:col-span-3 flex flex-col">
        <MotionFadeIn initialY={24} duration={0.8}>
          <div className="mb-4">
            <div className="flex flex-col gap-2">
              <h2
                className={cn(
                  'heading-lg transition-colors duration-300 group-hover:text-brand',
                  isDark ? 'on-dark' : 'text-primary',
                )}
              >
                {title}
              </h2>
              {subtitle && <p className="subtitle-lg text-brand">{subtitle}</p>}
            </div>
          </div>
        </MotionFadeIn>

        {text ? (
          <MotionFadeIn initialY={20} duration={0.8} delay={0.05}>
            <div className="max-w-4xl mb-4">
              <RichText
                data={text}
                enableGutter={false}
                enableProse={true}
                className="text-base md:text-lg"
              />
            </div>
          </MotionFadeIn>
        ) : null}

        {ctaEnabled ? (
          <MotionFadeIn initialY={20} duration={0.8} delay={0.1}>
            <div className="flex justify-start mt-4">
              <span
                className={cn(
                  'inline-flex items-center gap-2 font-ui font-medium text-base transition-colors duration-300',
                  isDark
                    ? 'text-text-white group-hover:text-gray-200'
                    : 'text-text-brand group-hover:text-brand/80',
                )}
              >
                <span className="uppercase">{ctaLabel}</span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="transition-transform duration-300 group-hover:translate-x-1"
                  aria-hidden="true"
                >
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          </MotionFadeIn>
        ) : null}
      </div>
    </Link>
  ) : null

  // Multi-product layout: stacked (original)
  const multiProductContent = !isSingleProduct || !singleProductImage ? (
    <>
      {Title}
      {Body}
      {Products}
      {CTALink}
    </>
  ) : null

  // If dark mode from settings or theme, use special dark section wrapper
  const isDarkTheme = theme === 'dark'
  if (isDarkMode || isDarkTheme) {
    return (
      <section data-theme="dark" className="bg-[var(--dark)]">
        <SectionWrapper background="transparent" isDarkMode={isDarkMode} padding={_padding}>
          <div id={id} className={scrollMarginClass}>
            {singleProductContent || multiProductContent}
          </div>
        </SectionWrapper>
      </section>
    )
  }

  return (
    <SectionWrapper background={sectionBackground} isDarkMode={isDarkMode} padding={_padding}>
      <div id={id} className={scrollMarginClass}>
        {singleProductContent || multiProductContent}
      </div>
    </SectionWrapper>
  )
}
